// import '@adornis/';
import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { BaseTypingAnimation, BaseTypingAnimationItem } from '../db/BaseTypingAnimation.js';

export const MarketingTypingAnimationExtension: BuildifyExtension<BaseTypingAnimation> = {
  class: BaseTypingAnimation,
  toolbar: {
    icon: 'i-cursor',
    text: {
      default: 'Typing Animation',
    },
  },
  group: 'Schriften',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-typing-animation
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-typing-animation>
    `;
  },
  editor({ content, contentController, controllerBaseKeyPath, host }) {
    const selectablesWrapper = [
      { tagName: 'd-text', displayName: 'Text' },
      { tagName: 'd-h1', displayName: 'Heading 1' },
      { tagName: 'd-h2', displayName: 'Heading 2' },
      { tagName: 'd-h3', displayName: 'Heading 3' },
    ] as Array<{ tagName: string; displayName: string }>;

    return html`
      <d-flex space="sm">
        <d-dropdown-selection
          clearable
          placeholder="Text-Typ"
          .selectables=${selectablesWrapper.map(wrapper => wrapper.tagName)}
          .renderString=${tagName => selectablesWrapper.find(wrapper => wrapper.tagName === tagName)?.displayName}
          ${contentController.field(...controllerBaseKeyPath, 'wrapperTag')}
        ></d-dropdown-selection>
        <d-dropdown-selection
          clearable
          placeholder="Text-Ausrichtung"
          .selectables=${['start', 'center', 'end']}
          ${contentController.field(...controllerBaseKeyPath, 'textAlign')}
        >
        </d-dropdown-selection>
        ${repeat(
          content.items ?? [],
          item => item._id,
          (item, index) => html`
            <d-flex space="sm" ${css({ marginBottom: host.spacing.lg })}>
              <d-input
                placeholder="Text hier eingeben"
                ${contentController.field(...controllerBaseKeyPath, 'items', index, 'word')}
              ></d-input>
              <d-buildify-color-picker
                placeholder="Textfarbe"
                ${contentController.field(...controllerBaseKeyPath, 'items', index, 'color')}
              ></d-buildify-color-picker>
            </d-flex>
            <d-icon-button
              ${css({ alignSelf: 'end' })}
              icon="trash"
              background=${host.colors.tone.warning}
              @click=${() => {
                content.items.splice(index, 1);
                host.requestUpdate();
              }}
            ></d-icon-button>
            <d-hr></d-hr>
          `,
        )}
        <d-icon-button
          ${css({ alignSelf: 'end' })}
          icon="plus"
          @click=${() => {
            content.items.push(new BaseTypingAnimationItem({}));
            host.requestUpdate();
          }}
        ></d-icon-button>
      </d-flex>
    `;
  },
};

@customElement('marketing-typing-animation')
export class MTypingAnimation extends ExtensionBoilerplate<BaseTypingAnimation> {
  @state({}) private _forwards: boolean = false;
  @state({}) private _viewPart: Maybe<string>;

  @state({}) private _index: number = 0;
  @state({}) private _offset: number = 0;

  @state({}) private _skipCount: number = 0;
  @state({}) private _skipDelay: number = 15;
  @state({}) private _speed: number = 70;

  @state({}) private _interval: Maybe<NodeJS.Timeout>;

  override connectedCallback(): void {
    super.connectedCallback();

    const words = (this.content.value?.items.length ? this.content.value.items : [{ word: 'Platzhalter :D' }]).map(
      item => item.word,
    );

    this._interval = setInterval(() => {
      if (this._forwards) {
        if (this._offset >= words[this._index]!.length) {
          ++this._skipCount;
          if (this._skipCount == this._skipDelay) {
            this._forwards = false;
            this._skipCount = 0;
          }
        }
      } else {
        if (this._offset == 0) {
          this._forwards = true;
          this._index++;
          this._offset = 0;
          if (this._index >= words.length) {
            this._index = 0;
          }
        }
      }
      this._viewPart = words[this._index]!.substr(0, this._offset);
      if (this._skipCount == 0) {
        if (this._forwards) {
          this._offset++;
        } else {
          this._offset--;
        }
      }
      // this.innerText = this.viewPart;
    }, this._speed);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this._interval) clearInterval(this._interval);
  }

  override _content(content: BaseTypingAnimation) {
    const viewPart = this._viewPart || '&nbsp;';

    const tagName = content.wrapperTag ?? `${DesignSystem.prefix}-text`;
    return html`
      ${unsafeHTML(`
      <${tagName} data-word-index="${this._index}">
        ${viewPart}
      </${tagName}>
    `)}
    `;
  }
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          width: '100%',
        },
        '[data-word-index]': {
          textAlign: this.content.value?.textAlign ?? 'start',
        },
        ...(this.content.value?.items ?? []).reduce((styles, item, index) => {
          styles[`[data-word-index="${index}"]`] = { color: item.color ?? '#333' };
          return styles;
        }, {} as Styles),
      },
    ] as Styles[];
  }
}
